import React, { useState, useMemo, useCallback, useRef } from 'react'
import { useDispatch } from 'react-redux'

import omit from 'lodash/omit'
import toLower from 'lodash/toLower'

import { useDialog } from '@galo-monitor/dialog'
import { useT } from '@galo-monitor/i18n'
import api from '@galo-monitor/services/apis/galoMonitorApi'
import { useSnackbar } from '@galo-monitor/snackbar'
import { CompanyActions } from '@galo-monitor/stores/company'
import colors from '@galo-monitor/styles/colors'
import DataTable from '@galo-monitor/web-components/DataTable'
import DataTableIcons from '@galo-monitor/web-components/DataTable/icons'
import Screen from '@galo-monitor/web-components/Screen'
import ClientCompanyModal from '@galo-monitor/web-containers/modals/company/ClientCompanyModal'
import ConfirmationModal from '@galo-monitor/web-containers/modals/ConfirmationModal'
import { useRoutes } from '@galo-monitor/web-containers/routes/authenticated'

const ClientCompaniesScreen = () => {
  const t = useT()
  const snackbar = useSnackbar()
  const { createDialog } = useDialog()
  const dispatch = useCallback(useDispatch(), [])
  const { routes, getActionsBySlug } = useRoutes()
  const tableRef = useRef(null)

  const [queryParams, setQueryParams] = useState({
    page: 1
  })

  const asyncClientCompaniesData = useCallback(
    params => api.get(
      '/companies/clients_companies',
      {
        params
      }
    ),
    []
  )

  const userActions = useMemo(
    () => getActionsBySlug(routes.clientCompanies.slug),
    [getActionsBySlug, routes.clientCompanies.slug]
  )

  const columns = useMemo(
    () => [
      {
        title: t('company client', { howMany: 1 }),
        field: 'name'
      },
      {
        title: t('responsible'),
        field: 'nameResponsible'
      },
      {
        title: t('email'),
        field: 'email'
      },
      {
        title: t('phone'),
        field: 'phone'
      }
    ],
    [t]
  )

  const onError = useCallback(
    err => snackbar.error(
      t(err?.response?.data?.message || 'unexpected error occurred'), {
        persist: false
      }),
    [snackbar, t]
  )

  const onSuccess = useCallback(() => {
    tableRef.current.onQueryChange()
  }, [])

  const onAddClick = useCallback(
    () => {
      createDialog({
        id: 'create-client-company',
        Component: ClientCompanyModal,
        props: {
          onSuccess
        }
      })
    },
    [createDialog, onSuccess]
  )

  const onEditClick = useCallback(
    (_, clientCompany) => {
      createDialog({
        id: 'edit-client-company',
        Component: ClientCompanyModal,
        props: {
          clientCompany,
          onSuccess
        }
      })
    },
    [createDialog, onSuccess]
  )

  const onSoftDeleteClick = useCallback(
    (_, clientCompany) => {
      const actionTitle = clientCompany.isActive ? {
        present: t('inactivate'),
        past: t('inactivated', { gender: 'female', howMany: 1 })
      } : {
        present: t('activate'),
        past: t('activated', { gender: 'female', howMany: 1 })
      }

      createDialog({
        id: 'confirmation',
        Component: ConfirmationModal,
        props: {
          title: t('are you sure you want to {action} the "{name}" {this}?', {
            gender: 'female',
            howMany: 1,
            this: t('company client', { howMany: 1 }),
            action: actionTitle.present,
            name: clientCompany.name
          }),
          buttonTitle: actionTitle.present,
          buttonProps: {
            color: clientCompany.isActive ? 'error' : 'primary'
          },
          onConfirm: () => {
            dispatch(CompanyActions.removeClientCompany(
              clientCompany,
              () => {
                snackbar.success(t('{this} {action} successfully!', {
                  this: t('company client', { howMany: 1 }),
                  action: toLower(actionTitle.past)
                }))
                onSuccess()
              },
              onError
            ))
          }
        }
      })
    },
    [createDialog, dispatch, onError, onSuccess, snackbar, t]
  )

  const onSoftDeleteChange = useCallback(
    filterInactive => {
      if(filterInactive) {
        setQueryParams(prevState => ({ ...prevState, active: false }))
        return
      }
      setQueryParams(omit(queryParams, 'active'))
    },
    [queryParams]
  )

  const actions = useMemo(
    () => {
      const datatableActions = []
      if (userActions.edit) {
        datatableActions.push(
          row => ({
            icon: DataTableIcons.Edit,
            tooltip: t('datagrid body edit tooltip'),
            onClick: onEditClick,
            iconButtonProps: {
              size: 'small',
              ...(!row.isActive ? { style: { display: 'none' } } : {})
            },
            position: 'row'
          })
        )
      }

      if (userActions.delete) {
        datatableActions.push(
          row => ({
            icon: row.isActive
              ? DataTableIcons.Delete
              : () => <DataTableIcons.Reply style={ { color: colors.red } } />,
            tooltip: row.isActive ? t('datagrid body inactivate tooltip') : t('datagrid body activate tooltip'),
            onClick: onSoftDeleteClick,
            iconButtonProps: {
              size: 'small'
            },
            position: 'row'
          })
        )
      }

      return datatableActions
    },
    [onEditClick, onSoftDeleteClick, t, userActions.delete, userActions.edit]
  )

  const crumbs = useMemo(() => [
    {
      title: routes.clientCompanies.title,
      key: 'clients-company-screen',
      url: '/companies/clients',
      active: true
    }
  ], [routes.clientCompanies.title])

  return (
    <Screen title={ routes.clientCompanies.title } breadcrumbs={ crumbs }>
      <DataTable
        tableRef={ tableRef }
        data={ asyncClientCompaniesData }
        columns={ columns }
        actions={ actions }
        onAddClick={ userActions.write && onAddClick }
        disabledRow={ rowData => !rowData.isActive }
        onSoftDeleteChange={ onSoftDeleteChange }
        customActionsAtLast
        softDelete
      />
    </Screen>
  )
}

export default ClientCompaniesScreen
